// @flow
import './index.less';

import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button, message } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useAuthState } from '_contexts/auth';
import { useCheckout, useCheckoutState } from '_contexts/checkout';
import usePromotionCode from '_hooks/usePromotionCode';

const validationSchema = yup.object().shape({
  promotionCode: yup.string().required().min(4),
});

type Props = {
  disabled?: boolean,
  type: 'checkout' | 'redeem',
  code?: string,
};

const PromotionCode = ({ disabled = false, type = 'checkout', code }: Props): React$Node => {
  const { t } = useTranslation();
  const { currentUser } = useAuthState();

  const { selectedPlan, promotionCode } = useCheckoutState();
  const { applyPromotionCode, resetPromotionCode } = useCheckout();
  const [emailCodeValidated, setEmailCodeValidated] = useState(false);

  const {
    promotionCode: promotionCodeData,
    error: checkPromotionCodeErr,
    validating,
    redeeming,
    validatePromotionCode,
    redeemPromotionCode,
  } = usePromotionCode({ currentUser, selectedPlan, type });

  const { register, handleSubmit, errors, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      promotionCode: promotionCodeData?.code,
    },
  });

  useEffect(() => {
    if (!code) return;
    validatePromotionCode(code);
  }, [code]);

  useEffect(() => {
    // reset promoCode if user signout after successful email validation
    if (emailCodeValidated && !currentUser) {
      setEmailCodeValidated(false);
      resetPromotionCode();
    }
  }, [emailCodeValidated, currentUser, resetPromotionCode]);

  const onSubmit = useCallback(
    (values) => {
      validatePromotionCode(values.promotionCode);
    },
    [validatePromotionCode]
  );

  const submitRedeemPromotionCode = useCallback(async () => {
    try {
      const promoCodeName = await redeemPromotionCode(promotionCodeData);
      message.success(t('SubscriptionSection_RedeemPromoCode_Success', { promoCodeName }));
    } catch (error) {
      message.error(error);
    }
  }, [t, redeemPromotionCode, promotionCodeData]);

  const error = useMemo(() => {
    if (errors.promotionCode?.message) {
      return errors.promotionCode.message;
    }

    if (checkPromotionCodeErr) {
      return t(checkPromotionCodeErr);
    }
  }, [t, errors, checkPromotionCodeErr]);

  useEffect(() => {
    applyPromotionCode(promotionCodeData);
  }, [applyPromotionCode, promotionCodeData]);

  if (promotionCode) {
    return (
      <div className="promotion-code-applied">
        <div className="message">
          <CheckCircleFilled style={{ color: '#b00015', marginRight: 5 }} />
          {t('CheckoutSection_PromotionCode_Summary', {
            code: promotionCode.code,
            name: promotionCode.coupon.name,
          })}
        </div>
        {type === 'redeem' && (
          <Button
            type="link"
            className="redeem-btn"
            disabled={disabled}
            loading={redeeming}
            onClick={() => submitRedeemPromotionCode()}
          >
            {t('Common_Confirm')}
          </Button>
        )}
        <Button type="link" className="remove-btn" disabled={disabled} onClick={resetPromotionCode}>
          {t('Common_Remove')}
        </Button>
      </div>
    );
  }

  return (
    <form className="promotion-code-form" onSubmit={handleSubmit(onSubmit)}>
      <div className={classNames('container', { disabled })}>
        <label className="label" htmlFor="promotion-code">
          {t('Label_PromotionCode')}
        </label>
        <input
          className="input"
          id="promotion-code"
          name="promotionCode"
          ref={register}
          disabled={disabled || validating}
        />
        <Button htmlType="submit" type="link" className="apply-btn" disabled={disabled} loading={validating}>
          {t('Common_Apply')}
        </Button>
      </div>
      {error && (
        <div className="error-message">
          <WarningFilled style={{ marginRight: 5 }} />
          {error}
        </div>
      )}
    </form>
  );
};

export default PromotionCode;
